import { useState, useEffect, useCallback } from "react";
import * as pharmaApi from "../services/pharmaApi";

/**
 * Hook to fetch customer statistics for PDV from PharmaFlow
 * @param {number} contactId - The Whaticket contact ID to fetch stats for
 * @returns {Object} - { stats, loading, error, refetch }
 */
const useCustomerStats = (contactId) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    if (!contactId) {
      setStats(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // First, get the PharmaFlow customer by Whaticket contactId
      let customer;
      try {
        customer = await pharmaApi.getCustomerByContactId(contactId);
      } catch (e) {
        // Customer not found in PharmaFlow - return empty stats
        setStats({
          totalOrders: 0,
          totalSpent: 0,
          averageTicket: 0,
          lastOrder: null,
          daysSinceLastOrder: null,
          favoritePaymentMethod: null,
          hasAddress: false,
          isIdentified: false,
          purchasesByCategory: [],
        });
        setLoading(false);
        return;
      }

      // Now get the stats using the PharmaFlow customer ID
      const data = await pharmaApi.getCustomerStats(customer.id);

      // Transform API response to match component expectations
      const transformedStats = {
        totalOrders: data.totalOrders || 0,
        totalSpent: data.totalSpent || 0,
        averageTicket: data.averageTicket || 0,
        lastOrder: data.lastOrderAt ? {
          total: data.lastOrderTotal,
          date: data.lastOrderAt
        } : null,
        daysSinceLastOrder: data.daysSinceLastOrder,
        favoritePaymentMethod: data.favoritePaymentMethod,
        hasAddress: customer.addresses && customer.addresses.length > 0,
        isIdentified: !!(customer.cpf || customer.cnpj),
        purchasesByCategory: data.purchasesByCategory || [],
      };

      setStats(transformedStats);
    } catch (err) {
      console.error("Error fetching customer stats:", err);
      setError(err.message || "Erro ao buscar estatísticas do cliente");
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  // Fetch on mount and when contactId changes
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    refetch: fetchStats
  };
};

export default useCustomerStats;
